import React, { useState, useMemo, useEffect } from "react";
import { DayOfWeek, SpecialEvent, HappyHourDeal, SubscriptionPlan } from "../types";
import { extractHappyHourFromImage } from "../services/geminiService";
import { SpecialCard } from "./SpecialCard";

interface SpecialEditorProps {
  onSave: (eventData: Partial<SpecialEvent>) => void;
  onCancel: () => void;
  plan: SubscriptionPlan;

  venueId: string; // UUID from Venues table
  venueName: string;

  initialData?: SpecialEvent | null;
  venueHeroImage?: string;
}

const VIBE_OPTIONS = [
  "Craft Beer", "Cocktails", "Trivia", "Live Music", "Cheap Eats",
  "Patio", "Rooftop", "Speakeasy", "Dog Friendly", "Sports",
];

const SCAN_FRIENDLY_ERROR = "Image upload could not be processed, please fill in the deals manually below.";

// UI pill order is Mon..Sun (indexes 0..6), but DB day numbers are Sun=0..Sat=6.
const WEEKDAYS: { idx: number; label: string }[] = [
  { idx: 0, label: "M" },
  { idx: 1, label: "T" },
  { idx: 2, label: "W" },
  { idx: 3, label: "TH" },
  { idx: 4, label: "F" },
  { idx: 5, label: "SA" },
  { idx: 6, label: "SU" },
];

const UI_INDEX_TO_DAY_NUMBER = [1, 2, 3, 4, 5, 6, 0];

function dayNumberToUiIndex(dayNumber: number): number | null {
  const idx = UI_INDEX_TO_DAY_NUMBER.indexOf(dayNumber);
  return idx >= 0 ? idx : null;
}

// Returns UI day indexes: 0..6 (Mon..Sun display order)
function normalizeDaysForEditor(initialData: any, prevDays: number[]): number[] {
  const raw =
    initialData?.active_days ??
    initialData?.activeDays ??
    initialData?.days ??
    [];

  if (!Array.isArray(raw) || raw.length === 0) return prevDays;

  // If numbers or numeric strings
  if (raw.every((x: any) => typeof x === "number" || /^\d+$/.test(String(x)))) {
    const nums = raw.map((x: any) => Number(x)).filter((n: any) => Number.isFinite(n));

     // Legacy DB style 1..7 (Sunday=7) support.
    const looksDb = nums.some((n) => n === 7);
    if (looksDb) {
      const ui = nums
        .map((n) => (n === 7 ? 0 : n))
        .map(dayNumberToUiIndex)
        .filter((n): n is number => n !== null)
        .filter((n) => n >= 0 && n <= 6);
      return ui.length ? Array.from(new Set(ui)).sort((a, b) => a - b) : prevDays;
    }

    const ui = nums
      .map(dayNumberToUiIndex)
      .filter((n): n is number => n !== null)
      .filter((n) => n >= 0 && n <= 6);
    return ui.length ? Array.from(new Set(ui)).sort((a, b) => a - b) : prevDays;
  }

  // If strings like "mon", "m", "th", etc. -> map to UI 0..6
  const map: Record<string, number> = {
    mon: 0, monday: 0, m: 0,
    tue: 1, tuesday: 1, t: 1,
    wed: 2, wednesday: 2, w: 2,
    thu: 3, thur: 3, thurs: 3, thursday: 3, th: 3,
    fri: 4, friday: 4, f: 4,
    sat: 5, saturday: 5, sa: 5,
    sun: 6, sunday: 6, su: 6,
  };

  const ui = raw
    .map((d: any) => map[String(d).trim().toLowerCase()])
    .filter((n: any) => Number.isFinite(n));

  return ui.length ? Array.from(new Set(ui)).sort((a, b) => a - b) : prevDays;
}

function uniqSorted(nums: number[]) {
  return Array.from(new Set(nums)).sort((a, b) => a - b);
}

// DB is int2[] 0..6 (Sun=0 ... Sat=6)
function uiDaysToDb(uiDays: any[] = []): number[] {
  const out = (uiDays || [])
    .map((d) => Number(d))
    .filter((n) => Number.isFinite(n))
    .map((n) => UI_INDEX_TO_DAY_NUMBER[n])
    .filter((n) => Number.isFinite(n))
    .filter((n) => n >= 0 && n <= 6);
  return uniqSorted(out);
}

function dbDaysToUi(dbDays: any[] = []): number[] {
  const out = (dbDays || [])
    .map((d) => Number(d))
    .filter((n) => Number.isFinite(n))
     .map((n) => (n === 7 ? 0 : n))
    .map(dayNumberToUiIndex)
    .filter((n): n is number => n !== null)
    .filter((n) => n >= 0 && n <= 6);
  return uniqSorted(out);
}

async function saveSpecialToSupabase(payload: any) {
  const res = await fetch("/.netlify/functions/save-special", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const raw = await res.text();
  let json: any = null;
  try { json = JSON.parse(raw); } catch {}

  console.log("[save-special] status", res.status, "raw:", raw);

  if (!res.ok) {
    throw new Error(json?.error || json?.message || raw || "Save failed");
  }

  // Your function may return { data: row } or { data: { special: row } }
  const data = json?.data?.special ?? json?.data ?? json;
  return data;
}

const SpecialEditor: React.FC<SpecialEditorProps> = ({
  onSave,
  onCancel,
  plan, // keep (unused)
  venueId,
  venueName,
  initialData,
  venueHeroImage,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scanFailureReason, setScanFailureReason] = useState<string | null>(null);
  const [showScanHelp, setShowScanHelp] = useState(false);
  const [isIndefinite, setIsIndefinite] = useState(true);
  const [customVibe, setCustomVibe] = useState("");
  const [invalidPriceIds, setInvalidPriceIds] = useState<string[]>([]);

  const [formData, setFormData] = useState({
    id: "", // IMPORTANT: used for edit
    specialType: "",
    startTime: "16:00",
    endTime: "19:00",
    days: [0, 1, 2, 3, 4], // Mon–Fri (UI format: 0..6)
    deals: [
      { id: "init-deal-1", item: "Sample Item", price: "$5", category: "Beer", isFeatured: true },
    ] as HappyHourDeal[],
    tags: ["Craft Beer"] as string[],
    imageUrl:
      venueHeroImage ||
      "https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?q=80&w=2070&auto=format&fit=crop",
    endDate: null as string | null,
    notes: "",
  });

// Load existing special into form (EDIT MODE)
useEffect(() => {
  if (!initialData) return;

  setFormData((prev) => ({
    ...prev,
    id: initialData.id || "",
    specialType:
      (initialData as any).title ||
      (initialData as any).specialType ||
      (initialData as any).name ||
      "",
    startTime:
      (initialData as any).start_time ||
      (initialData as any).startTime ||
      "16:00",
    endTime:
      (initialData as any).end_time ||
      (initialData as any).endTime ||
      "19:00",

    // 🔥 THIS IS THE CRITICAL FIX
    days: normalizeDaysForEditor(initialData, prev.days),

    deals: (initialData as any).deals || prev.deals,
    tags: (initialData as any).tags || prev.tags,
    imageUrl:
      (initialData as any).image_url ||
      (initialData as any).imageUrl ||
      prev.imageUrl,
    endDate:
      (initialData as any).end_date ??
      (initialData as any).endDate ??
      null,
    notes:
      (initialData as any).fine_print ||
      (initialData as any).notes ||
      "",
  }));

  const endDate =
    (initialData as any).end_date ??
    (initialData as any).endDate ??
    null;

  setIsIndefinite(endDate === null || endDate === undefined);
}, [initialData]);

  const customVibesInTags = useMemo(() => {
    return formData.tags.filter((tag) => !VIBE_OPTIONS.includes(tag));
  }, [formData.tags]);

  const badgeSelectedCount = useMemo(() => {
    return (formData.deals || []).filter((d: any) => !!d.isFeatured).length;
  }, [formData.deals]);

  const handleMenuImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLoading(true);
    setError(null);
    setScanFailureReason(null);
    setShowScanHelp(false);

    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const extracted = await extractHappyHourFromImage(
          (reader.result as string).split(",")[1],
          file.type
        );

       // If Gemini returns days, normalize to editor UI indexes.
        let nextDays = formData.days;
        if (Array.isArray(extracted.days) && extracted.days.length) {
          nextDays = normalizeDaysForEditor({ days: extracted.days }, formData.days);
        }

        setFormData((prev) => ({
          ...prev,
          deals: extracted.deals.map((d: any, i: number) => ({
            ...d,
            isFeatured: i < 5,
            isGroupDeal: false,
          })),
          startTime: extracted.startTime || prev.startTime,
          endTime: extracted.endTime || prev.endTime,
          days: nextDays,
        }));

        setInvalidPriceIds([]);
      } catch (err: any) {
        setScanFailureReason(err?.message || "Menu scan failed");
        setError(SCAN_FRIENDLY_ERROR);
      } finally {
        setLoading(false);
      }
    };

    reader.readAsDataURL(file);
  };

  const toggleDay = (idx: number) => {
    setFormData((prev) => ({
      ...prev,
      days: prev.days.includes(idx)
        ? prev.days.filter((d) => d !== idx)
        : [...prev.days, idx].sort((a, b) => a - b),
    }));
  };

  const toggleVibe = (vibe: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.includes(vibe) ? prev.tags.filter((t) => t !== vibe) : [...prev.tags, vibe],
    }));
  };

  const handleAddCustomVibe = () => {
    const v = customVibe.trim();
    if (!v) return;

    if (customVibesInTags.length >= 3) {
      setError("Maximum of 3 custom vibes reached.");
      return;
    }

    if (!formData.tags.includes(v)) {
      setFormData((prev) => ({ ...prev, tags: [...prev.tags, v] }));
    }

    setCustomVibe("");
    setError(null);
  };

  const removeCustomVibe = (vibe: string) => {
    setFormData((prev) => ({ ...prev, tags: prev.tags.filter((t) => t !== vibe) }));
  };

  const addNewDeal = (isGroup: boolean = false) => {
    const newDeal: any = {
      id: Date.now().toString(),
      item: "",
      price: isGroup ? "" : "$",
      category: "Beer",
      isFeatured: formData.deals.length < 5,
      isGroupDeal: isGroup,
    };

    setFormData((prev) => ({ ...prev, deals: [...prev.deals, newDeal] }));
  };

  const updateDeal = (id: string, field: keyof HappyHourDeal, value: any) => {
    setFormData((prev) => ({
      ...prev,
      deals: prev.deals.map((d: any) => (d.id === id ? { ...d, [field]: value } : d)),
    }));
  };

  const toggleDealFeatured = (id: string, checked: boolean) => {
    setFormData((prev) => {
      const selectedCount = prev.deals.filter((d: any) => !!d.isFeatured).length;
      return {
        ...prev,
        deals: prev.deals.map((d: any) => {
          if (d.id !== id) return d;
          if (checked && !d.isFeatured && selectedCount >= 5) return d;
          return { ...d, isFeatured: checked };
        }),
      };
    });
  };
  const updatePrice = (id: string, value: string, isGroup: boolean = false) => {
    let clean = value;
    if (!isGroup) {
      if (value === "") clean = "$";
      else if (value.length > 0 && !value.startsWith("$")) clean = "$" + value;
    }
    updateDeal(id, "price" as any, clean);
    setInvalidPriceIds((prev) => prev.filter((pId) => pId !== id));
  };

  const removeDeal = (id: string) => {
    if (formData.deals.length === 1) return;
    setFormData((prev) => ({ ...prev, deals: prev.deals.filter((d: any) => d.id !== id) }));
    setInvalidPriceIds((prev) => prev.filter((pId) => pId !== id));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!venueId || String(venueId).trim() === "") {
      setError("Missing venueId. The app isn’t passing the venue UUID into SpecialEditor yet.");
      return;
    }

    const missingPriceDeals = (formData.deals || []).filter((d: any) => {
      const isGroup = !!d.isGroupDeal;
      const cleanPrice = isGroup
        ? String(d.price || "").trim()
        : String(d.price || "").replace("$", "").trim();
      return cleanPrice === "";
    });

    if (missingPriceDeals.length > 0) {
      setInvalidPriceIds(missingPriceDeals.map((d: any) => d.id));
      setError("Please add a price for each item before publishing.");
      const firstInvalid = document.getElementById(`deal-container-${missingPriceDeals[0].id}`);
      if (firstInvalid) firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    if ((formData.days || []).length === 0) {
      setError("Please select at least one day of the week.");
      return;
    }

    // DEBUG
    console.log("[SpecialEditor] formData.days RAW:", formData.days);

    const uiDays = (formData.days || [])
      .map((d: any) => Number(d))
      .filter((n: any) => Number.isFinite(n) && n >= 0 && n <= 6);

    console.log("[SpecialEditor] uiDays cleaned (0..6):", uiDays);
    
    // formData.days is UI indexes 0..6
    const activeDays = uiDaysToDb(formData.days); // -> DB numbers 0..6
    console.log("[SpecialEditor] active_days going to DB (0..6):", activeDays);

    const validDeals = (formData.deals || []).filter((d: any) => String(d.item || "").trim() !== "");

    const payload = {
      // IMPORTANT: if id exists, save-special should UPDATE, not INSERT
      id: formData.id ? formData.id : null,
      venue_id: venueId,
      title: (formData.specialType || "").trim() || null,
      active_days: activeDays, // number[]
      start_time: formData.startTime || null,
      end_time: formData.endTime || null,
      is_indefinite: isIndefinite,
      end_date: isIndefinite ? null : formData.endDate,
      fine_print: (formData.notes || "").trim() || null,
      deals: validDeals,
      is_active: true,
      is_published: true,
      image_url: formData.imageUrl || null,
      tags: formData.tags || [],
    };

    try {
      setSaving(true);
      console.log("[SpecialEditor] saving payload", payload);

      const saved = await saveSpecialToSupabase(payload);
      console.log("[SpecialEditor] saved row", saved);

      // Keep the editor in sync with the returned id (important for subsequent edits)
      const savedId = saved?.id || payload.id || "";
      if (savedId && savedId !== formData.id) {
        setFormData((prev) => ({ ...prev, id: savedId }));
      }

      setInvalidPriceIds([]);
      setError(null);

      onSave({
        ...formData,
        id: savedId,
        // store DB value in-memory if your app expects it:
        active_days: activeDays,
        deals: validDeals,
        endDate: isIndefinite ? null : formData.endDate,
      });

      onCancel();
    } catch (err: any) {
      console.error("[SpecialEditor] save failed", err);
      setError(err?.message || "Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-24 md:py-40">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-8 mb-16 text-left">
        <div className="flex items-center gap-6">
          <button
            onClick={onCancel}
            className="w-14 h-14 rounded-full border-2 border-black/5 flex items-center justify-center hover:bg-black hover:text-white transition-all shadow-sm"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h2 className="text-4xl md:text-5xl font-black serif tracking-tight">
              {initialData ? "Edit" : "Post a"} <span className="text-amber-600">Special.</span>
            </h2>
            <p className="text-gray-400 font-black uppercase tracking-widest text-[10px] mt-2">
              Publishing for: {venueName}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-8 space-y-12">
          <div className="bg-black rounded-[3.5rem] p-10 md:p-14 text-white relative overflow-hidden shadow-2xl grainy border-4 border-amber-500">
            <div className="relative z-10 space-y-8">
              <div>
                <span className="px-4 py-1.5 bg-amber-500 text-black rounded-full font-black text-[9px] uppercase tracking-widest mb-4 inline-block">
                  Flash Entry Mode
                </span>
                <h3 className="text-4xl md:text-6xl font-black serif tracking-tighter leading-none">
                  Scan Your <span className="italic text-amber-500">Menu.</span>
                </h3>
                <p className="text-gray-400 font-medium text-sm md:text-lg mt-4 max-w-xl">
                  Upload a menu image and let AI extract items, prices, and times.
                </p>
              </div>
              <label className="group inline-flex items-center gap-6 px-10 py-6 bg-white text-black rounded-3xl font-black text-lg uppercase tracking-widest cursor-pointer hover:bg-amber-500 transition-all active:scale-95">
                <div className="w-10 h-10 rounded-full bg-black text-white flex items-center justify-center group-hover:bg-white group-hover:text-black transition-colors">
                  {loading ? (
                    <div className="w-5 h-5 border-4 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
                    </svg>
                  )}
                </div>
                {loading ? "ANALYZING MENU..." : "UPLOAD & SCAN MENU"}
                <input type="file" accept="image/*" className="hidden" onChange={handleMenuImageUpload} disabled={loading} />
              </label>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="bg-white p-8 md:p-14 rounded-[3.5rem] shadow-2xl border border-black/[0.03] grainy space-y-16 text-left">
            {error && (
              <div className="p-5 bg-red-50 text-red-600 rounded-2xl border border-red-100 space-y-3">
                <p className="text-[10px] font-black uppercase tracking-widest">{error}</p>

                {error === SCAN_FRIENDLY_ERROR && (
                  <div className="space-y-3">
                    <button
                      type="button"
                      onClick={() => setShowScanHelp((prev) => !prev)}
                      className="inline-flex items-center rounded-full border border-red-200 bg-white px-3 py-1 text-[10px] font-black uppercase tracking-widest text-red-600 hover:bg-red-50"
                    >
                      Why did it fail?
                    </button>

                    {showScanHelp && (
                      <div className="rounded-xl bg-white border border-red-100 p-4 text-[10px] font-bold tracking-wide text-gray-600 normal-case space-y-2">
                        <p className="font-black text-gray-800">Common reasons:</p>
                        <ul className="list-disc pl-4 space-y-1">
                          <li>Low light, glare, or blur on handwritten/chalkboard text.</li>
                          <li>Photo taken too far away, angled, or partially cut off.</li>
                          <li>Very dense formatting where prices and items are hard to separate.</li>
                        </ul>
                        <p className="font-black text-gray-800 pt-1">Tips for better scans:</p>
                        <ul className="list-disc pl-4 space-y-1">
                          <li>Take the photo straight-on with bright, even lighting.</li>
                          <li>Fill the frame with only the menu/chalkboard.</li>
                          <li>Use a higher-resolution image and avoid screenshots when possible.</li>
                        </ul>
                        {scanFailureReason && (
                          <p className="pt-2 text-[9px] text-gray-400 break-words">Technical detail: {scanFailureReason}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* BASIC INFO */}
            <div className="space-y-10">
              <h3 className="text-2xl font-black serif uppercase tracking-tight underline decoration-amber-500 decoration-4 underline-offset-8">
                1. Basic Info
              </h3>

              <div className="space-y-6">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">
                    Headline / Special Title
                  </label>
                  <input
                    required
                    type="text"
                    value={formData.specialType}
                    onChange={(e) => setFormData({ ...formData, specialType: e.target.value })}
                    placeholder="e.g. Afternoon Happy Hour"
                    className="w-full h-16 bg-gray-50 border-2 border-transparent focus:border-amber-500 rounded-2xl px-8 font-black text-lg outline-none transition-all shadow-inner"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">Start Time</label>
                    <input
                      type="time"
                      value={formData.startTime}
                      onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                      className="w-full h-16 bg-gray-50 rounded-2xl px-8 font-black text-lg outline-none shadow-inner"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">End Time</label>
                    <input
                      type="time"
                      value={formData.endTime}
                      onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                      className="w-full h-16 bg-gray-50 rounded-2xl px-8 font-black text-lg outline-none shadow-inner"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">Active Days</label>
                  <div className="flex flex-wrap gap-2">
                    {WEEKDAYS.map((day) => (
                      <button
                        key={day.idx}
                        type="button"
                        onClick={() => toggleDay(day.idx)}
                        className={`w-12 h-12 rounded-xl text-[10px] font-black transition-all border-2 ${
                          formData.days.includes(day.idx)
                            ? "bg-black text-white border-black"
                            : "bg-gray-50 text-gray-300 border-transparent hover:border-black/10"
                        }`}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="pt-6 border-t border-black/5">
                  <div className="flex items-center justify-between mb-4">
                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">
                      How long is this happy hour running for?
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="indefinite"
                        checked={isIndefinite}
                        onChange={(e) => setIsIndefinite(e.target.checked)}
                        className="w-4 h-4 rounded text-amber-500"
                      />
                      <label htmlFor="indefinite" className="text-[10px] font-black uppercase tracking-widest text-black cursor-pointer">
                        Indefinite (Until removed)
                      </label>
                    </div>
                  </div>

                  {!isIndefinite && (
                    <div>
                      <input
                        type="date"
                        required={!isIndefinite}
                        value={formData.endDate || ""}
                        onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                        className="w-full h-14 bg-gray-50 border-2 border-transparent focus:border-amber-500 rounded-2xl px-6 font-bold text-sm outline-none shadow-inner"
                      />
                      <p className="text-[8px] font-bold text-gray-400 uppercase tracking-widest mt-2 ml-4">
                        This post will be hidden from searches after this date.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* VIBES */}
            <div className="space-y-10 pt-10 border-t border-black/5">
              <h3 className="text-2xl font-black serif uppercase tracking-tight underline decoration-amber-500 decoration-4 underline-offset-8">
                2. Vibes & Tags
              </h3>

              <div className="space-y-8">
                <div className="space-y-4">
                  <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">Select From List</label>
                  <div className="flex flex-wrap gap-2">
                    {VIBE_OPTIONS.map((vibe) => (
                      <button
                        key={vibe}
                        type="button"
                        onClick={() => toggleVibe(vibe)}
                        className={`px-4 py-2 rounded-full text-[9px] font-black uppercase tracking-widest transition-all border-2 ${
                          formData.tags.includes(vibe)
                            ? "bg-amber-500 text-black border-amber-500"
                            : "bg-gray-50 text-gray-400 border-transparent hover:border-black/10"
                        }`}
                      >
                        {vibe}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">Custom Vibes (Up to 3)</label>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {customVibesInTags.map((vibe) => (
                      <div key={vibe} className="px-4 py-2 bg-black text-white rounded-full flex items-center gap-3 text-[9px] font-black uppercase tracking-widest">
                        {vibe}
                        <button type="button" onClick={() => removeCustomVibe(vibe)} className="hover:text-amber-500 transition-colors">
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>

                  {customVibesInTags.length < 3 && (
                    <div className="relative group">
                      <input
                        type="text"
                        value={customVibe}
                        onChange={(e) => setCustomVibe(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddCustomVibe())}
                        placeholder="Type vibe and click add..."
                        className="w-full h-14 bg-gray-50 border-2 border-transparent focus:border-amber-500 rounded-2xl px-6 font-bold text-sm outline-none shadow-inner"
                      />
                      <button
                        type="button"
                        onClick={handleAddCustomVibe}
                        className="absolute right-3 top-1/2 -translate-y-1/2 bg-amber-500 text-black px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest shadow-lg hover:bg-black hover:text-white transition-all"
                      >
                        Add Vibe
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* MENU */}
            <div className="space-y-10 pt-10 border-t border-black/5">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-black serif uppercase tracking-tight underline decoration-amber-500 decoration-4 underline-offset-8">
                  3. The Menu
                </h3>
              </div>

              <div className="space-y-6">
                <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400 ml-1">
                  Pick up to 5 items to show on your badge preview and on Explore cards.
                </p>
                {formData.deals.map((deal: any) => (
                  <div
                    key={deal.id}
                    id={`deal-container-${deal.id}`}
                    className={`p-6 rounded-3xl border border-black/[0.03] grainy space-y-4 ${
                      deal.isGroupDeal ? "bg-amber-50/30 ring-1 ring-amber-500/10" : "bg-gray-50/50"
                    }`}
                  >
                    {/* (same deal UI as you already had) */}
                    {/* keep the rest of your deal rendering unchanged if you want */}
                    <div className="flex flex-col md:flex-row gap-4 md:items-end">
                      {/* ... */}
                      <div className="flex-[4] space-y-1">
                        <label className="text-[8px] font-black uppercase tracking-widest text-gray-400 ml-2">
                          {deal.isGroupDeal ? 'Group Deal Label' : 'Item Name'}
                        </label>
                        <input
                          placeholder={deal.isGroupDeal ? 'e.g. All Draft Pitchers / Select Appetizers' : 'e.g. Signature Margarita'}
                          value={deal.item}
                          onChange={(e) => updateDeal(deal.id, "item" as any, e.target.value)}
                          className="w-full h-14 bg-white rounded-xl px-6 font-bold text-sm outline-none shadow-inner"
                        />
                      </div>

                      <div className="flex-[1] space-y-1">
                        <label className="text-[8px] font-black uppercase tracking-widest text-gray-400 ml-2">Price</label>
                        <input
                          placeholder={deal.isGroupDeal ? "e.g. $5 Off / 2-for-1 / 20% Off" : "$5"}
                          value={deal.price}
                          onChange={(e) => updatePrice(deal.id, e.target.value, !!deal.isGroupDeal)}
                          className={`w-full h-14 bg-white rounded-xl px-4 font-bold text-sm outline-none shadow-inner text-center border-2 transition-all ${
                            invalidPriceIds.includes(deal.id) ? "border-red-500 bg-red-50" : "border-transparent"
                          }`}
                        />
                        {invalidPriceIds.includes(deal.id) && (
                          <p className="text-[8px] font-black uppercase text-red-600 text-center animate-pulse">Required</p>
                        )}
                      </div>

                      <div className="flex-[2] space-y-1">
                        <label className="text-[8px] font-black uppercase tracking-widest text-gray-400 ml-2">{deal.isGroupDeal ? 'Deal Category' : 'Category'}</label>
                        <select
                          value={deal.category}
                          onChange={(e) => updateDeal(deal.id, "category" as any, e.target.value as any)}
                          className="w-full h-14 bg-white rounded-xl px-4 font-black text-[9px] uppercase tracking-widest outline-none shadow-inner"
                        >
                          <option value="Beer">Beer</option>
                          <option value="Wine">Wine</option>
                          <option value="Cocktail">Cocktail</option>
                          <option value="Food">Food</option>
                        </select>
                      </div>

                      <button type="button" onClick={() => removeDeal(deal.id)} className="h-14 flex items-center justify-center text-red-300 hover:text-red-500 transition-colors px-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7" />
                        </svg>
                      </button>
                    </div>

                    <div className="flex items-center justify-between px-2 pt-1">
                      <label className={`text-[9px] font-black uppercase tracking-widest ${deal.isFeatured ? 'text-black' : 'text-gray-500'}`}>
                        Add to badge?
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={!!deal.isFeatured}
                          disabled={!deal.isFeatured && badgeSelectedCount >= 5}
                          onChange={(e) => toggleDealFeatured(deal.id, e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-amber-500 disabled:opacity-40"
                        />
                        {!deal.isFeatured && badgeSelectedCount >= 5 && (
                          <span className="text-[8px] font-bold uppercase tracking-widest text-gray-400">Max 5 selected</span>
                        )}
                      </div>
                    </div>
                    
                    {deal.isGroupDeal && (
                      <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400 px-2 opacity-70">
                        Group deal format: use label + offer (e.g. “All Draft Pitchers” + “$5 Off”).
                      </p>
                    )}
                  </div>
                ))}

                <div className="flex flex-col md:flex-row gap-4">
                  <button type="button" onClick={() => addNewDeal(false)} className="flex-1 py-8 border-2 border-dashed border-black/10 rounded-[2rem] hover:border-amber-500 hover:bg-amber-50 transition-all">
                    <span className="text-[11px] font-black uppercase tracking-widest text-gray-400">Add Individual Item</span>
                  </button>

                  <button type="button" onClick={() => addNewDeal(true)} className="flex-1 py-8 border-2 border-dashed border-amber-500/20 rounded-[2rem] hover:border-amber-500 hover:bg-amber-50 transition-all">
                    <span className="text-[11px] font-black uppercase tracking-widest text-amber-600">Add Category/Group Deal</span>
                  </button>
                </div>
              </div>
            </div>

            {/* FINE PRINT */}
            <div className="space-y-10 pt-10 border-t border-black/5">
              <h3 className="text-2xl font-black serif uppercase tracking-tight underline decoration-amber-500 decoration-4 underline-offset-8">
                4. Fine Print
              </h3>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">Notes (optional)</label>
                <textarea
                  maxLength={200}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="e.g. valid with purchase of a drink, limit 2 per customer, etc."
                  className="w-full h-32 bg-gray-50 border-2 border-transparent focus:border-amber-500 rounded-2xl p-6 font-medium text-sm outline-none transition-all shadow-inner resize-none"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={saving}
              className={`w-full h-24 rounded-[2rem] font-black text-xl uppercase tracking-[0.2em] transition-all shadow-2xl active:scale-[0.98] ${
                saving ? "bg-black/60 text-white cursor-wait" : "bg-black text-white hover:bg-amber-500 hover:text-black"
              }`}
            >
              {saving ? "Publishing…" : "Publish Special Now"}
            </button>
          </form>
        </div>

        <div className="lg:col-span-4 space-y-10 lg:sticky lg:top-32 h-fit text-left">
          <div className="bg-amber-50 border border-amber-200 p-8 rounded-[3rem] shadow-sm">
            <h3 className="text-xl font-black serif uppercase tracking-tight leading-none mb-4">
              Live <span className="text-amber-600">Preview.</span>
            </h3>

            <div className="scale-90 md:scale-100 origin-top">
              <SpecialCard
                event={
                  {
                    ...formData,
                    venueName,
                    active_days: uiDaysToDb(formData.days),
                    address: "Your Venue Address",
                    rating: 5,
                    name: formData.specialType,
                  } as any
                }
                hideFavorite
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpecialEditor;
